import { useEffect, useMemo } from 'react';
import { useTranslation } from '@doccareplus/i18n';
import { Button, SearchSelect, Skeleton } from '@doccareplus/ui';
import { profileInitials, profileName } from '../domain/profiles';
import { useSelectProfileCommand } from '../application/profiles.command';
import { usePortalProfilesQuery } from '../application/profiles.query';
import { applyActiveProfileHeader } from '../infrastructure/portalProfiles.api';

export interface PortalProfileSwitcherProps {
  onFindProfiles: () => void;
  compact?: boolean;
}

export function PortalProfileSwitcher({ onFindProfiles, compact = false }: PortalProfileSwitcherProps) {
  const { t } = useTranslation('portal');
  const profiles = usePortalProfilesQuery();
  const select = useSelectProfileCommand();
  const options = useMemo(
    () =>
      (profiles.data?.profiles ?? []).map((profile) => ({
        value: profile.linkId,
        label: `${profile.displayAlias ? `${profile.displayAlias} · ` : ''}${profile.patientDisplayName}`,
        description: profile.clinicName,
        avatarText: profileInitials(profileName(profile)),
      })),
    [profiles.data?.profiles]
  );

  useEffect(() => {
    if (profiles.data?.activeLinkId) {
      applyActiveProfileHeader(profiles.data.activeLinkId);
    }
  }, [profiles.data?.activeLinkId]);

  if (profiles.isLoading) {
    return <Skeleton className={compact ? 'h-10 w-full' : 'h-[66px] w-full'} rounded />;
  }

  if (options.length === 0) {
    return (
      <Button variant="outline" size="sm" fullWidth onClick={onFindProfiles}>
        {t('profile.findRecords', 'Find my clinic records')}
      </Button>
    );
  }

  return (
    <SearchSelect
      value={profiles.data?.activeLinkId ?? null}
      options={options}
      onChange={(linkId) => select.mutate(linkId)}
      variant="rich"
      appearance="select"
      searchable={options.length > 4}
      showDescriptionInTrigger
      size={compact ? 'sm' : 'md'}
      label={compact ? undefined : t('profile.viewingProfile', 'Viewing profile')}
      placeholder={t('profile.chooseProfile', 'Choose a clinic profile')}
      triggerAriaLabel={t('profile.viewingProfile', 'Viewing profile')}
      disabled={select.isPending}
      footer={
        <Button variant="ghost" size="sm" fullWidth onClick={onFindProfiles}>
          {t('profile.findAnotherClinic', 'Find my records at another clinic')}
        </Button>
      }
    />
  );
}
